import { format } from 'date-fns';

export const MEDICATION_JSON_PREFIX = 'MEDICATION_ORDER_JSON::';

export const SCHEDULE_KEYS = ['morning', 'afternoon', 'evening', 'night'];

const SCHEDULE_LABELS = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
  night: 'Night',
};

const FOOD_LABELS = {
  before: 'Before Food',
  with: 'With Food',
  after: 'After Food',
  none: 'Not Meal Dependent',
};

// Route - Medical abbreviations
export const ROUTES = [
  'PO',   // Per os (by mouth)
  'IV',   // Intravenous
  'IM',   // Intramuscular
  'SC',   // Subcutaneous
  'SL',   // Sublingual
  'INH',  // Inhalation
  'TOP',  // Topical
  'PR',   // Per rectum
  'GT',   // Gastrostomy tube
  'PEG',  // Percutaneous endoscopic gastrostomy
  'TD',   // Transdermal
];

// Schedule type: fixed time slots (morning/afternoon/evening/night) or interval (every X hours)
export const SCHEDULE_TYPE = {
  TIME_SLOTS: 'timeSlots',
  INTERVAL: 'interval',
};

// Interval options for "every X hours" (e.g. every 4h → 10am, 2pm, 6pm, 10pm)
export const INTERVAL_HOURS_OPTIONS = [2, 4, 6, 8, 12];

// Time Slots - Simplified timing options
export const TIME_SLOTS = {
  MULTIPLE: ['morning', 'afternoon', 'evening', 'night'],
  ONLY: ['onlyMorning', 'onlyAfternoon', 'onlyEvening', 'onlyNight'],
};

export const TIME_SLOT_LABELS = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
  night: 'Night',
  onlyMorning: 'Only Morning',
  onlyAfternoon: 'Only Afternoon',
  onlyEvening: 'Only Evening',
  onlyNight: 'Only Night',
};

// Food Timing - Simplified food relation
export const FOOD_TIMING_OPTIONS = [
  { value: 'beforeFood', label: 'Before Food' },
  { value: 'afterFood', label: 'After Food' },
  { value: 'notMealDependent', label: 'Not Meal Dependent' },
];

// Migration function: Convert old frequency/timing to new timeSlots/foodTiming (and optional interval)
export function migrateOldTimingToNew(item) {
  // If already has interval schedule, return with interval fields
  if (item.scheduleType === 'interval' && (item.intervalHours || item.firstDoseTime)) {
    return {
      scheduleType: 'interval',
      intervalHours: item.intervalHours || 4,
      firstDoseTime: item.firstDoseTime || '08:00',
      timeSlots: [],
      foodTiming: item.foodTiming || 'notMealDependent',
    };
  }
  // If already has new structure (timeSlots), return as-is
  if (item.timeSlots || item.foodTiming) {
    return {
      scheduleType: item.scheduleType || 'timeSlots',
      intervalHours: item.intervalHours,
      firstDoseTime: item.firstDoseTime,
      timeSlots: Array.isArray(item.timeSlots) ? item.timeSlots : [],
      foodTiming: item.foodTiming || 'notMealDependent',
    };
  }

  let timeSlots = [];
  let foodTiming = 'notMealDependent';

  // Migrate from old frequency
  if (item.frequency) {
    const freq = item.frequency.toUpperCase();
    switch (freq) {
      case 'OD':
      case 'QD':
        timeSlots = ['onlyMorning'];
        foodTiming = 'afterFood';
        break;
      case 'BD':
      case 'BID':
        timeSlots = ['morning', 'evening'];
        foodTiming = 'afterFood';
        break;
      case 'TID':
        timeSlots = ['morning', 'afternoon', 'evening'];
        foodTiming = 'afterFood';
        break;
      case 'QID':
        timeSlots = ['morning', 'afternoon', 'evening'];
        foodTiming = 'afterFood';
        break;
      case 'HS':
        timeSlots = ['onlyNight'];
        foodTiming = 'notMealDependent';
        break;
      case 'PRN':
      case 'SOS':
        timeSlots = ['onlyMorning'];
        foodTiming = 'notMealDependent';
        break;
      case 'STAT':
        timeSlots = ['onlyMorning'];
        foodTiming = 'notMealDependent';
        break;
      default:
        // For q2h, q3h, q4h, q6h, q8h, q12h, EOD - default to morning
        timeSlots = ['onlyMorning'];
        foodTiming = 'afterFood';
    }
  }

  // Migrate from old timing qualifiers
  if (item.timingQualifier && Array.isArray(item.timingQualifier)) {
    if (item.timingQualifier.includes('AC')) {
      foodTiming = 'beforeFood';
    } else if (item.timingQualifier.includes('PC')) {
      foodTiming = 'afterFood';
    }
    if (item.timingQualifier.includes('AM') && timeSlots.length === 0) {
      timeSlots = ['onlyMorning'];
    } else if (item.timingQualifier.includes('PM') && timeSlots.length === 0) {
      timeSlots = ['onlyEvening'];
    } else if (item.timingQualifier.includes('Nocte') && timeSlots.length === 0) {
      timeSlots = ['onlyNight'];
    }
  }

  // Migrate from old schedule
  if (item.schedule && Array.isArray(item.schedule) && item.schedule.length > 0) {
    const schedule = item.schedule;
    if (schedule.includes('morning') && schedule.includes('afternoon') && schedule.includes('evening') && schedule.includes('night')) {
      timeSlots = ['morning', 'afternoon', 'evening', 'night'];
    } else if (schedule.includes('morning') && schedule.includes('afternoon') && schedule.includes('evening')) {
      timeSlots = ['morning', 'afternoon', 'evening'];
    } else if (schedule.includes('morning') && schedule.includes('evening')) {
      timeSlots = ['morning', 'evening'];
    } else if (schedule.includes('morning')) {
      timeSlots = ['onlyMorning'];
    } else if (schedule.includes('afternoon')) {
      timeSlots = ['onlyAfternoon'];
    } else if (schedule.includes('evening')) {
      timeSlots = ['onlyEvening'];
    } else if (schedule.includes('night')) {
      timeSlots = ['onlyNight'];
    }
  }

  // Migrate from old food field
  if (item.food) {
    if (item.food === 'before') {
      foodTiming = 'beforeFood';
    } else if (item.food === 'after') {
      foodTiming = 'afterFood';
    } else if (item.food === 'none') {
      foodTiming = 'notMealDependent';
    }
  }

  // Default if nothing found
  if (timeSlots.length === 0) {
    timeSlots = ['onlyMorning'];
  }

  return { scheduleType: 'timeSlots', timeSlots, foodTiming };
}

export function extractMedicationPayload(messageText = '') {
  if (!messageText) {
    return { payload: null, summary: '' };
  }

  const index = messageText.lastIndexOf(MEDICATION_JSON_PREFIX);
  if (index === -1) {
    return { payload: null, summary: messageText.trim() };
  }

  const summary = messageText.slice(0, index).trim();
  const rawJson = messageText.slice(index + MEDICATION_JSON_PREFIX.length).trim();

  if (!rawJson) {
    return { payload: null, summary };
  }

  try {
    const payload = JSON.parse(rawJson);
    return { payload, summary };
  } catch (error) {
    console.warn('Failed to parse structured medication payload:', error);
    return { payload: null, summary: messageText.trim() };
  }
}

export function formatScheduleList(scheduleArray = [], t = null) {
  if (!Array.isArray(scheduleArray) || scheduleArray.length === 0) {
    return t ? t('medication.labels.asDirected') : 'As directed';
  }

  return scheduleArray
    .map((key) => {
      if (t) {
        const translationKey = `medication.labels.schedule.${key}`;
        const translated = t(translationKey, { defaultValue: SCHEDULE_LABELS[key] || key });
        return translated;
      }
      return SCHEDULE_LABELS[key] || key;
    })
    .join(', ');
}

export function scheduleObjectToArray(scheduleObject = {}) {
  return SCHEDULE_KEYS.filter((key) => !!scheduleObject[key]);
}

export function scheduleArrayToObject(scheduleArray = []) {
  return SCHEDULE_KEYS.reduce((acc, key) => {
    acc[key] = scheduleArray.includes(key);
    return acc;
  }, {});
}

export function formatFoodInstruction(value, t = null) {
  if (!value) {
    return t ? t('medication.labels.asDirected') : 'As directed';
  }

  if (t) {
    const translationKey = `medication.labels.foodInstruction.${value}`;
    const translated = t(translationKey, { defaultValue: FOOD_LABELS[value] || value });
    return translated;
  }

  return FOOD_LABELS[value] || value;
}

// Format route - returns the abbreviation as-is
export function formatRoute(value) {
  if (!value) {
    return '';
  }
  return value.toUpperCase();
}

// Format time slots - returns formatted string
export function formatTimeSlots(timeSlots = [], t = null) {
  if (!Array.isArray(timeSlots) || timeSlots.length === 0) {
    return t ? t('medication.labels.asDirected') : 'As directed';
  }

  return timeSlots
    .map((slot) => {
      if (t) {
        const translationKey = `medication.labels.timeSlot.${slot}`;
        const translated = t(translationKey, { defaultValue: TIME_SLOT_LABELS[slot] || slot });
        return translated;
      }
      return TIME_SLOT_LABELS[slot] || slot;
    })
    .join(', ');
}

// Format food timing - returns formatted string
export function formatFoodTiming(foodTiming, t = null) {
  if (!foodTiming) {
    return t ? t('medication.labels.asDirected') : 'As directed';
  }

  const option = FOOD_TIMING_OPTIONS.find(opt => opt.value === foodTiming);
  if (!option) {
    return foodTiming;
  }

  if (t) {
    const translationKey = `medication.labels.foodTiming.${foodTiming}`;
    return t(translationKey, { defaultValue: option.label });
  }

  return option.label;
}

// Legacy format functions (for backward compatibility during migration)
export function formatFrequency(value) {
  if (!value) {
    return '';
  }
  return value;
}

export function formatTimingQualifiers(valueArray = []) {
  if (!Array.isArray(valueArray) || valueArray.length === 0) {
    return '';
  }
  return valueArray.filter(Boolean).join(', ');
}

/**
 * Compute reminder times for a medication with interval schedule (e.g. every 4h starting at 10:00).
 * Returns array of { hour, minute } for today (24h wrap).
 * @param {number} intervalHours - e.g. 4
 * @param {string} firstDoseTime - "HH:mm" e.g. "10:00"
 * @returns {Array<{hour: number, minute: number}>}
 */
export function getIntervalReminderTimes(intervalHours, firstDoseTime) {
  if (!intervalHours || intervalHours < 1 || intervalHours > 24) return [];
  const [h, m] = (firstDoseTime || '08:00').split(':').map(Number);
  const startMinutes = (h * 60 + (m || 0)) % (24 * 60);
  const times = [];
  for (let i = 0; i < 24; i++) {
    const totalMins = startMinutes + i * intervalHours * 60;
    const dayMins = totalMins % (24 * 60);
    times.push({
      hour: Math.floor(dayMins / 60),
      minute: dayMins % 60,
    });
    if (i > 0 && dayMins <= startMinutes) break; // wrapped to next day
  }
  return times;
}

/**
 * Check if current time (or given date) is within a few minutes of a reminder time.
 * @param {Date} now
 * @param {Array<{hour, minute}>} reminderTimes
 * @param {number} toleranceMinutes - e.g. 1
 */
export function isReminderTime(now, reminderTimes, toleranceMinutes = 1) {
  const currentMins = now.getHours() * 60 + now.getMinutes();
  return reminderTimes.some(({ hour, minute }) => {
    const reminderMins = hour * 60 + minute;
    const diff = Math.abs(currentMins - reminderMins);
    return diff <= toleranceMinutes || diff >= 24 * 60 - toleranceMinutes;
  });
}

export function parseLegacyMedicationDetails(messageText = '', subject = '') {
  if (!messageText && !subject) {
    return null;
  }

  const text = messageText || '';
  const subjectText = subject || '';

  let name = 'Medication';
  let dosage = '';
  let frequency = '';
  let instructions = text.trim();

  if (subjectText) {
    const subjectParts = subjectText.split(/[,\-]/);
    if (subjectParts.length > 0) {
      const candidate = subjectParts[0].trim();
      if (candidate) {
        name = candidate;
      }
    }
  }

  const dosageMatch = text.match(/(\d+(?:\s*(?:mg|mcg|g|ml|tablet(?:s)?|capsule(?:s)?)))/i);
  if (dosageMatch) {
    dosage = dosageMatch[0];
  }

  const frequencyMatch = text.match(/(once|twice|three times|four times|daily|every \d+\s*hours?|morning|evening|night|bedtime)/i);
  if (frequencyMatch) {
    frequency = frequencyMatch[0];
  }

  if (subjectText && subjectText.includes(',')) {
    const parts = subjectText.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length > 0) {
      name = parts[0];
    }
    if (parts.length > 1 && !dosage) {
      dosage = parts[1];
    }
    if (parts.length > 2 && !frequency) {
      frequency = parts[2];
    }
  }

  return {
    name: name || 'Medication',
    dosage: dosage || '',
    frequency: frequency || '',
    instructions: instructions || '',
  };
}

export function buildPrescriptionSummary(payload, options = {}) {
  if (!payload) {
    return '';
  }

  const {
    includeHeader = true,
    includeInstructions = true,
  } = options;

  const lines = [];

  if (includeHeader) {
    lines.push('MEDICATION PRESCRIPTION');
    lines.push('----------------------------------------');
  }

  if (payload.organizationName) {
    lines.push(`Organization: ${payload.organizationName}`);
  }
  if (payload.organizationAddress) {
    lines.push(`Address: ${payload.organizationAddress}`);
  }
  if (payload.organizationContact) {
    lines.push(`Contact: ${payload.organizationContact}`);
  }

  if (payload.patient) {
    const parts = [];
    if (payload.patient.name) {
      parts.push(payload.patient.name);
    }
    if (payload.patient.id) {
      parts.push(`ID: ${payload.patient.id}`);
    }
    if (parts.length > 0) {
      lines.push(`Patient: ${parts.join(' • ')}`);
    }
  }

  if (payload.doctor) {
    const doctorParts = [];
    if (payload.doctor.name) {
      doctorParts.push(payload.doctor.name);
    }
    if (payload.doctor.registrationNumber) {
      doctorParts.push(`Reg: ${payload.doctor.registrationNumber}`);
    }
    if (payload.doctor.phone) {
      doctorParts.push(`Phone: ${payload.doctor.phone}`);
    }
    if (doctorParts.length > 0) {
      lines.push(`Doctor: ${doctorParts.join(' • ')}`);
    }
  }

  if (payload.issuedAt) {
    lines.push(`Issued: ${format(new Date(payload.issuedAt), 'MMMM dd, yyyy • h:mm a')}`);
  }

  if (payload.validUntil) {
    lines.push(`Valid Until: ${format(new Date(payload.validUntil), 'MMMM dd, yyyy')}`);
  }

  if (payload.medications && payload.medications.length > 0) {
    lines.push('');
    lines.push('Medications:');

    payload.medications.forEach((item, index) => {
      const number = `${index + 1}.`;
      const medLineParts = [];

      if (item.name) {
        medLineParts.push(item.name);
      }
      if (item.form) {
        medLineParts.push(item.form);
      }
      if (item.strength) {
        medLineParts.push(item.strength);
      }

      const medLine = medLineParts.length > 0
        ? `${number} ${medLineParts.join(' • ')}`
        : `${number} Medication`;
      lines.push(medLine);

      if (item.dose) {
        lines.push(`   Dose: ${item.dose}`);
      }

      if (item.quantity) {
        lines.push(`   Quantity: ${item.quantity}`);
      }

      if (item.route) {
        lines.push(`   Route: ${formatRoute(item.route)}`);
      }

      // Use new timeSlots and foodTiming structure (with migration fallback)
      const timing = migrateOldTimingToNew(item);
      if (timing.timeSlots && timing.timeSlots.length > 0) {
        lines.push(`   Timing: ${formatTimeSlots(timing.timeSlots)}`);
      }
      if (timing.foodTiming) {
        lines.push(`   Food: ${formatFoodTiming(timing.foodTiming)}`);
      }

      // Legacy support (for old prescriptions)
      if (!timing.timeSlots || timing.timeSlots.length === 0) {
        if (item.frequency) {
          lines.push(`   Frequency: ${formatFrequency(item.frequency)}`);
        }
        if (item.schedule && item.schedule.length > 0) {
          lines.push(`   Timing: ${formatScheduleList(item.schedule)}`);
        }
        if (item.timingQualifier && item.timingQualifier.length > 0) {
          const qualifiers = formatTimingQualifiers(item.timingQualifier);
          if (qualifiers) {
            lines.push(`   Timing Qualifiers: ${qualifiers}`);
          }
        }
        if (item.food) {
          lines.push(`   Food: ${formatFoodInstruction(item.food)}`);
        }
      }

      if (item.duration) {
        lines.push(`   Duration: ${item.duration}`);
      }

      if (item.notes) {
        lines.push(`   Notes: ${item.notes}`);
      }
    });
  }

  if (includeInstructions && payload.additionalInstructions) {
    lines.push('');
    lines.push('Additional Instructions:');
    lines.push(payload.additionalInstructions);
  }

  if (includeInstructions && payload.pharmacyNotes) {
    lines.push('');
    lines.push('Pharmacy Notes:');
    lines.push(payload.pharmacyNotes);
  }

  return lines.join('\n').trim();
}


